import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./Grade2Zhuyin.css";

/** Shuffle helper */
function shuffleArray(arr) {
  const cloned = [...arr];
  for (let i = cloned.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cloned[i], cloned[j]] = [cloned[j], cloned[i]];
  }
  return cloned;
}

/**
 * Grade2Zhuyin:
 *  - Focus on more advanced Zhuyin usage: multi-syllable words, short sentences
 *  - Single quiz pool (25 questions) => Quick Quiz picks random, Final Quiz uses all
 *  - Two-column layout: left = lesson content, right = quick & final quiz
 */

// Example advanced Zhuyin lesson snippet
const advancedZhuyinNotes = (
  <div className="g2z-advanced-notes">
    <h4>Advanced Zhuyin (Bopomofo) Notes</h4>
    <p>
      By Grade 2, we focus on combining multiple Zhuyin syllables into words or short sentences.
      Practice forming 2-3+ character phrases with correct tone marks.
    </p>
    <ul>
      <li>Example: ㄒㄧㄠˇ ㄌㄨㄥˊ ㄅㄠ (小笼包)</li>
      <li>Mind tone changes: ㄧ → ㄧˊ or ㄧˇ in different contexts.</li>
      <li>Practice reading short sentences: ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄔ ㄈㄢˋ (我喜欢吃饭: “I like to eat meals.”)</li>
      <li>Keep refining your tone accuracy with more complex phrases.</li>
    </ul>
  </div>
);

// Single Zhuyin-based quiz pool (25 questions)
const zhuyinPool = [
  {
    question: "How might you write '我喜欢吃饭' (I like to eat) in Zhuyin?",
    correctAnswer: "ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄔ ㄈㄢˋ",
    options: [
      "ㄨㄛˋ ㄏㄨㄢ ㄔ ㄈㄢˇ",
      "ㄨㄛˇ ㄒㄧˇ ㄏㄨㄢ ㄔ ㄈㄢˋ",
      "ㄨㄛˇ ㄒㄧㄣ ㄏㄨㄢˋ ㄔㄨˇ"
    ]
  },
  {
    question: "Which tone mark is used for the 4th (falling) tone in Zhuyin?",
    correctAnswer: "ˋ",
    options: [
      "ˉ",
      "ˇ",
      "ˋ"
    ]
  },
  {
    question: "Combining multiple syllables: ㄒㄧㄠˇ + ㄌㄨㄥˊ + ㄅㄠ => means?",
    correctAnswer: "xiǎo lóng bāo (小笼包), a type of soup dumpling",
    options: [
      "A sweet dessert",
      "A type of soup dumpling",
      "A fruit drink"
    ]
  },
  {
    question: "If you see ㄑㄧˇ ㄨㄣˋ, it suggests 'qǐ wèn' (起问) or politely 'qǐngwèn'?",
    correctAnswer: "Actually 'qǐngwèn' => ㄑㄧㄥˇ ㄨㄣˋ is correct for 'Excuse me...'",
    options: [
      "No difference, they’re the same phrase",
      "It's 'qǐ wèn' meaning 'start asking'",
      "Actually 'qǐngwèn' => ㄑㄧㄥˇ ㄨㄣˋ is correct"
    ]
  },
  {
    question: "Tone sandhi can occur with ㄧ or ㄧˇ => changes to a second tone in certain contexts. Why?",
    correctAnswer: "To avoid adjacent identical or conflicting tones for smoother speech",
    options: [
      "It never changes tone",
      "Just a random rule with no reason",
      "To avoid adjacent identical/conflicting tones"
    ]
  },
  {
    question: "Which phrase might say 'I can speak a little Chinese' in Zhuyin?",
    correctAnswer: "ㄨㄛˇ ㄏㄨㄟˋ ㄕㄨㄛ ㄧˉ ㄉㄧㄢˇ ㄓㄨㄥ ㄨㄣˊ",
    options: [
      "ㄨㄛˇ ㄏㄨㄟˋ ㄕㄨㄛ ㄧˉ ㄉㄧㄢˇ ㄓㄨㄥ ㄨㄣˊ",
      "ㄋㄧˇ ㄇㄟˊ ㄧㄡˇ ㄕㄨㄛ ㄏㄨㄟˋ",
      "ㄨㄛˇ ㄧㄡˇ ㄏㄞˇ ㄑㄧㄢˊ"
    ]
  },
  {
    question: "In daily usage, ㄒㄧㄠˇ ㄎㄡˇ ㄏㄨㄟˋ => 'xiǎo kǒu huì' might mean?",
    correctAnswer: "A small gathering or meeting",
    options: [
      "No meaning at all",
      "A large festival",
      "A small gathering or meeting"
    ]
  },
  {
    question: "Which symbol combination is a correct syllable: ㄅ + ㄠ = ㄅㄠ. The tone mark goes?",
    correctAnswer: "Above or after ㄠ, e.g., ㄅㄠˇ if 3rd tone",
    options: [
      "We don’t mark tones with ㄠ",
      "Below ㄅ always",
      "Above/after ㄠ (e.g. ㄅㄠˇ for 3rd tone)"
    ]
  },
  {
    question: "To say 'Zhuyin is fun' => ㄓㄨˋ ㄧㄣ ㄏㄠˇ ㄨㄢˊ => means?",
    correctAnswer: "Zhuyin is fun/enjoyable to learn",
    options: [
      "I hate Zhuyin now",
      "Zhuyin is fun to learn",
      "Zhuyin is a typed code only"
    ]
  },
  {
    question: "Multi-syllable example: ㄔˋ ㄩㄣˊ => might represent 'chì yún' (赤云) meaning 'red cloud'? Tone context needed. This shows?",
    correctAnswer: "Zhuyin combos can represent many words—tone + context matter",
    options: [
      "Tones are irrelevant in Zhuyin",
      "No multi-syllable combos exist",
      "Tone + context matter for correct meaning"
    ]
  },
  {
    question: "ㄨㄛˇ ㄅㄨˋ ㄒㄧㄤˇ ㄔ ㄌㄚˋ ㄐㄧㄠˋ => means 'I don't want to eat chili pepper.' The 'ㄅㄨˋ' is what tone?",
    correctAnswer: "4th tone typically (bù), except before 4th tone (changes to 2nd)",
    options: [
      "Always 1st tone",
      "Always 3rd tone",
      "4th tone, but can shift to 2nd before another 4th tone"
    ]
  },
  {
    question: "If you see ㄊㄧㄝˇ ㄍㄨㄢ ㄧㄣ (铁观音), it's a tea name. The 'ㄊㄧㄝˇ' suggests which tone?",
    correctAnswer: "3rd (tiě) if the character is 铁 meaning 'iron'",
    options: [
      "1st tone only",
      "3rd tone if iron (tiě)",
      "4th tone always"
    ]
  },
  {
    question: "Which of these indicates a 2-syllable word in Zhuyin with correct tone marks?",
    correctAnswer: "ㄏㄨㄛˇ ㄍㄨㄛ (huǒ guō for 'hot pot')",
    options: [
      "ㄏㄨㄢˇ ㄍㄨㄛˇ (huǎn guǒ) no real meaning",
      "ㄏㄨㄛˇ ㄍㄨㄛ (huǒ guō, hot pot)",
      "ㄏㄨㄛˋ ㄍㄨㄛˋ no tones needed"
    ]
  },
  {
    question: "How to say 'I want to rest a bit' => ㄨㄛˇ ㄒㄧㄤˇ ㄒㄧㄡ ㄒㄧ ㄧˉ ㄉㄧㄢˇ => wǒ xiǎng xiūxī yī diǎn?",
    correctAnswer: "'I’d like to rest a bit'—tones confirm meaning",
    options: [
      "It means 'I want to dance now'",
      "'I’d like to rest a bit', tone usage clarifies meaning",
      "No sense in actual usage"
    ]
  },
  {
    question: "Zhuyin for '句子' (jùzi) might be ㄐㄩˋ ㄗ˙. The second syllable is neutral. Why?",
    correctAnswer: "Often 'zi' endings are neutral toned (˙) or lightly pronounced",
    options: [
      "We never use tone on second syllable",
      "Zhuyin can’t show second tones at all",
      "Often 'zi' is neutral or lightly toned"
    ]
  },
  {
    question: "ㄎㄜˇ ㄧˇ => kěyǐ means 'can/may.' If you say ㄋㄧˇ ㄎㄜˇ ㄧˇ ㄅㄤ ㄨㄛˇ ㄇㄚ˙ => 'Can you help me?'",
    correctAnswer: "Yes, it’s a polite request in Zhuyin form",
    options: [
      "No, it means 'I must help you'",
      "Yes, it’s a polite request: 'Can you help me?'",
      "Means 'where is the toilet?'"
    ]
  },
  {
    question: "Tone practice: ㄓㄨˋ ㄋㄧˇ ㄏㄠˇ ㄩㄣˋ => 'zhù nǐ hǎo yùn' means?",
    correctAnswer: "'Wish you good luck'",
    options: [
      "'See you next time'",
      "'Wish you good luck'",
      "No standard meaning"
    ]
  },
  {
    question: "Combining ㄓ + ㄨㄥ => ㄓㄨㄥ + tone => can be 'zhōng' (1st tone), 'zhóng' (2nd) or 'zhǒng' (3rd), 'zhòng' (4th). Confirms that...",
    correctAnswer: "One Zhuyin combo can have multiple tones => multiple words",
    options: [
      "We always use 1 tone only",
      "Zhuyin can’t handle multiple tones",
      "Same Zhuyin letters, different tones => different words"
    ]
  },
  {
    question: "ㄇㄧˋ ㄠ (mì ao) might form 'miào' (4th tone) or 'mǐao' (3rd tone). Why is context key?",
    correctAnswer: "Identical consonant/vowel combos can differ in tone => different meaning",
    options: [
      "Tone is optional for these combos",
      "All combos mean the same",
      "Different tone => different meaning"
    ]
  },
  {
    question: "If you see ㄕㄤˋ ㄏㄞˇ => 'shànghǎi' (Shanghai). Tones?",
    correctAnswer: "4th tone + 3rd tone",
    options: [
      "1st + 1st",
      "2nd + 4th",
      "4th + 3rd"
    ]
  },
  {
    question: "In advanced reading, ㄓㄨˋ ㄧㄣ helps you parse characters by sound. This fosters?",
    correctAnswer: "Better pronunciation & bridging to full character reading",
    options: [
      "Ignoring tone usage",
      "Confusing simpler reading",
      "Better pronunciation & bridging to full character reading"
    ]
  },
  {
    question: "A short advanced phrase: ㄨㄛˇ ㄇㄚˇ ㄕㄤˋ ㄌㄞˊ => wǒ mǎ shàng lái means?",
    correctAnswer: "'I’m coming immediately' or 'I’ll come right away'",
    options: [
      "'I’m going to sleep now'",
      "'I’m not arriving today'",
      "'I’ll come right away'",
    ]
  },
  {
    question: "Why is multi-syllable practice so important at Grade 2 Zhuyin level?",
    correctAnswer: "It mirrors real Mandarin usage, linking tones & phrases for fluency",
    options: [
      "We don’t need multi-syllable reading yet",
      "Only single characters matter",
      "It mirrors real usage, linking tones for fluency"
    ]
  }
];

export default function Grade2Zhuyin() {
  // We only teach Zhuyin => single topic
  const [quiz, setQuiz] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const [finalQuizQuestions, setFinalQuizQuestions] = useState([]);
  const [finalQuizActive, setFinalQuizActive] = useState(false);
  const [finalQuizIndex, setFinalQuizIndex] = useState(0);
  const [finalQuizScore, setFinalQuizScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(300); // 5 min

  // Timer for final quiz
  useEffect(() => {
    if (finalQuizActive && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFinalQuizActive(false);
    }
  }, [finalQuizActive, timeLeft]);

  // Generate a random Quick Quiz question
  const generateQuiz = () => {
    const q = zhuyinPool[Math.floor(Math.random() * zhuyinPool.length)];
    setQuiz({
      question: q.question,
      correctAnswer: q.correctAnswer,
      options: shuffleArray([...q.options])
    });
    setSelectedAnswer(null);
    setFeedback(null);
  };

  // Handle Quick Quiz answer
  const handleAnswerClick = (answer) => {
    setSelectedAnswer(answer);
    if (quiz && answer === quiz.correctAnswer) {
      setFeedback("✅ Correct!");
    } else {
      setFeedback("❌ Try Again!");
    }
    // Load next question after short delay
    setTimeout(() => {
      generateQuiz();
    }, 1500);
  };

  // Start Final Quiz => use entire zhuyinPool
  const startFinalQuiz = () => {
    const finalSet = shuffleArray(zhuyinPool);
    setFinalQuizQuestions(finalSet);
    setFinalQuizActive(true);
    setFinalQuizIndex(0);
    setFinalQuizScore(0);
    setTimeLeft(300);
  };

  // Handle Final Quiz answer
  const handleFinalQuizAnswer = (choice) => {
    if (finalQuizQuestions[finalQuizIndex].correctAnswer === choice) {
      setFinalQuizScore(prev => prev + 1);
    }
    if (finalQuizIndex < finalQuizQuestions.length - 1) {
      setFinalQuizIndex(prev => prev + 1);
    } else {
      setFinalQuizActive(false);
    }
  };

  return (
    <div className="g2chi-page">
      <h1 className="g2chi-title">Grade 2 Zhuyin (Advanced)</h1>
      <p className="g2chi-description">
        Learn multi-syllable words, short sentences, and tone practice with Bopomofo!
      </p>

      <div className="g2chi-lesson-content">
        <h2 className="g2chi-lesson-title">Zhuyin (Advanced)</h2>
        <p className="g2chi-lesson-description">
          By Grade 2, you’ll combine multiple characters in Zhuyin to form everyday expressions.
          Focus on linking tones smoothly.
        </p>
        <p className="g2chi-lesson-example">
          <strong>Example:</strong> ㄊㄧㄝˇ ㄍㄨㄢ ㄧㄣ (铁观音) for a tea name—mind those tones!
        </p>
        {advancedZhuyinNotes}
      </div>

      {/* QUIZ Section */}
      <div className="g2chi-quiz-double-container">
        {/* Quick Quiz */}
        <div className="g2chi-quick-quiz-container">
          <h2 className="g2chi-quiz-title">Quick Quiz!</h2>
          {!quiz && (
            <button className="g2chi-quiz-btn" onClick={generateQuiz}>
              Start Quick Quiz
            </button>
          )}
          {quiz && (
            <>
              <p className="g2chi-quiz-question">{quiz.question}</p>
              <div className="g2chi-quiz-options">
                {quiz.options.map((opt, i) => (
                  <button
                    key={i}
                    className={`g2chi-quiz-btn ${selectedAnswer === opt ? "selected" : ""}`}
                    onClick={() => handleAnswerClick(opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              {feedback && <p className="g2chi-quiz-feedback">{feedback}</p>}
            </>
          )}
        </div>

        {/* Final Quiz */}
        <div className="g2chi-final-quiz-container">
          <h2 className="g2chi-quiz-title">Final Quiz</h2>
          {!finalQuizActive && (
            <button className="g2chi-final-quiz-button" onClick={startFinalQuiz}>
              Start Final Quiz (25 Questions)
            </button>
          )}
          {finalQuizActive && finalQuizQuestions.length > 0 && (
            <div className="g2chi-final-quiz-content">
              <p>
                Time Left: {Math.floor(timeLeft / 60)}:
                {String(timeLeft % 60).padStart(2, "0")}
              </p>
              <p>
                Question {finalQuizIndex + 1} of {finalQuizQuestions.length}
              </p>
              <p className="g2chi-quiz-question">
                {finalQuizQuestions[finalQuizIndex].question}
              </p>
              <div className="g2chi-quiz-options">
                {finalQuizQuestions[finalQuizIndex].options.map((choice, idx) => (
                  <button key={idx} onClick={() => handleFinalQuizAnswer(choice)}>
                    {choice}
                  </button>
                ))}
              </div>
              <p>Score: {finalQuizScore}</p>
              {finalQuizIndex === finalQuizQuestions.length - 1 && !timeLeft && (
                <p>Final Score: {finalQuizScore}/{finalQuizQuestions.length}</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Link to="/ChineseSubject" className="g2chi-back-button">
        ← Back to Chinese Page
      </Link>
    </div>
  );
}
